import { useState } from "react";
import { Check, FlaskConical } from "lucide-react";
import { useResolvedLoanRule } from "@/api/hooks";
import type {
  Location,
  LoanRuleLevel,
  PatronCategory,
  RecordType,
  ResolvedLoanRule,
} from "@/api/types";
import { cn } from "@/lib/utils";
import { formatEuros, formatFineCap, plural } from "../../loanRules";
import { SelectField } from "./fields";
import { RECORD_TYPES, RECORD_TYPE_PLURAL_LABELS } from "@/lib/recordTypes";

/**
 * Answers "which rule applies?" for an item type at a location, and shows the
 * resolution order so administrators understand why.
 */
export function RuleSimulator({
  category,
  locations,
}: {
  category: PatronCategory;
  locations: Location[];
}) {
  const [itemType, setItemType] = useState<RecordType>("book");
  const [locationId, setLocationId] = useState<string | undefined>(locations[0]?.id);
  const { data, isLoading, error } = useResolvedLoanRule(category.id, itemType, locationId);

  const locationName = (id: string | null) =>
    id === null
      ? "Tous les sites"
      : (locations.find((location) => location.id === id)?.name ?? "Site");

  return (
    <section
      aria-labelledby="rule-simulator-title"
      className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <header className="mb-5 flex items-start gap-3">
        <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
          <FlaskConical className="h-4 w-4" aria-hidden />
        </span>
        <div>
          <h2
            id="rule-simulator-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            Simulateur
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Quelle règle s&apos;applique quand un lecteur « {category.name} » emprunte… ?
          </p>
        </div>
      </header>

      {locations.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Créez d&apos;abord un site dans l&apos;administration des établissements.
        </p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label="Type de document"
                value={itemType}
                onChange={(value) => {
                  setItemType(value as RecordType);
                }}
                options={RECORD_TYPES.map((type) => ({
                  value: type,
                  label: RECORD_TYPE_PLURAL_LABELS[type],
                }))}
              />
              <SelectField
                label="Site du prêt"
                value={locationId ?? ""}
                onChange={setLocationId}
                options={locations.map((location) => ({
                  value: location.id,
                  label: location.name,
                }))}
              />
            </div>

            {data && (
              <ol aria-label="Ordre de priorité des règles" className="space-y-1.5">
                {data.levels.map((level, index) => (
                  <LevelRow
                    key={index}
                    rank={index + 1}
                    level={level}
                    matchedBefore={data.levels.slice(0, index).some((previous) => previous.matched)}
                    label={levelLabel(level, category, locationName)}
                  />
                ))}
              </ol>
            )}
          </div>

          <div aria-live="polite">
            {isLoading && (
              <div
                aria-busy="true"
                className="h-full min-h-48 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50"
              />
            )}
            {error && (
              <p role="alert" className="text-sm text-red-700 dark:text-red-400">
                {error.message}
              </p>
            )}
            {data && <RuleSummary rules={data.rules} />}
          </div>
        </div>
      )}
    </section>
  );
}

function levelLabel(
  level: LoanRuleLevel,
  category: PatronCategory,
  locationName: (id: string | null) => string
): string {
  if (level.kind === "category") return `Règles par défaut « ${category.name} »`;
  const what = level.itemType ? RECORD_TYPE_PLURAL_LABELS[level.itemType] : "Tous les documents";
  return `${what} · ${locationName(level.locationId)}`;
}

function LevelRow({
  rank,
  level,
  label,
  matchedBefore,
}: {
  rank: number;
  level: LoanRuleLevel;
  label: string;
  matchedBefore: boolean;
}) {
  let status: string;
  if (level.matched) status = "Appliquée";
  else if (level.kind === "category") status = "Non utilisée";
  else if (level.policyId === null) status = "Aucune exception";
  else if (level.policyActive === false) status = "Désactivée";
  else status = matchedBefore ? "Masquée par une règle plus précise" : "—";

  return (
    <li
      aria-current={level.matched ? "true" : undefined}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
        level.matched
          ? "bg-primary/10 ring-primary/40 text-gray-900 ring-1 dark:text-white"
          : "text-gray-500 dark:text-gray-400"
      )}
    >
      <span
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
          level.matched
            ? "bg-primary text-primary-foreground"
            : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
        )}
      >
        {level.matched ? <Check className="h-3.5 w-3.5" aria-hidden /> : rank}
      </span>
      <span className={cn("min-w-0 flex-1", level.matched && "font-medium")}>{label}</span>
      <span className="text-xs whitespace-nowrap">{status}</span>
    </li>
  );
}

function RuleSummary({ rules }: { rules: ResolvedLoanRule["rules"] }) {
  if (!rules.loanable) {
    return (
      <div className="flex h-full min-h-48 items-center justify-center rounded-xl border border-dashed border-gray-300 p-6 text-center dark:border-gray-600">
        <div>
          <p className="text-base font-semibold text-gray-900 dark:text-white">Non empruntable</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Consultation sur place uniquement.
          </p>
        </div>
      </div>
    );
  }

  const renewals =
    rules.renewalsAllowed && rules.maxRenewals > 0
      ? {
          value: `${String(rules.maxRenewals)} × ${String(rules.renewalDays)} j`,
          detail: `dès ${plural(rules.renewalWindowDays, "jour")} avant l'échéance`,
        }
      : { value: "Non", detail: "pas de prolongation" };

  const tiles = [
    {
      label: "Durée du prêt",
      value: plural(rules.loanDays, "jour"),
      detail: `quota ${String(rules.maxLoans)} documents`,
    },
    { label: "Prolongation", ...renewals },
    {
      label: "Réservation",
      value: rules.reservationsAllowed ? "Oui" : "Non",
      detail: rules.reservationsAllowed ? "file d'attente ouverte" : "réservations fermées",
    },
    {
      label: "Retard",
      value: `${formatEuros(rules.finePerDayInCents)}/j`,
      detail:
        `plafond ${formatFineCap(rules.maxFineInCents)}` +
        (rules.gracePeriodDays > 0 ? ` · tolérance ${plural(rules.gracePeriodDays, "jour")}` : ""),
    },
  ];

  return (
    <dl className="grid grid-cols-2 gap-3">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
        >
          <dt className="text-xs tracking-wide text-gray-500 uppercase dark:text-gray-400">
            {tile.label}
          </dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900 tabular-nums dark:text-white">
            {tile.value}
          </dd>
          <dd className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{tile.detail}</dd>
        </div>
      ))}
    </dl>
  );
}
